import { useToast } from "@adafia/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateFileNode } from "@/notes/api/notes";
import type { FileNode } from "@/notes/types";

export const useUpdateNote = () => {
	const queryClient = useQueryClient();
	const { showToast } = useToast();

	return useMutation({
		mutationFn: ({
			id,
			content,
			title,
			isPublished,
		}: {
			id: string;
			content?: string | null;
			title?: string;
			isPublished?: boolean;
		}) => updateFileNode(id, content, title, isPublished),
		onMutate: async (variables) => {
			const treeCaresAboutChanges =
				variables.title !== undefined || variables.isPublished !== undefined;

			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			if (treeCaresAboutChanges) {
				await queryClient.cancelQueries({ queryKey: ["tree"] });
			}
			await queryClient.cancelQueries({
				queryKey: ["getNode", variables.id],
			});

			// Snapshot the previous values
			const previousTree = treeCaresAboutChanges
				? queryClient.getQueryData<FileNode[]>(["tree"])
				: undefined;
			const previousNote = queryClient.getQueryData<FileNode>([
				"getNode",
				variables.id,
			]);

			// Optimistically update the tree cache
			if (previousTree && treeCaresAboutChanges) {
				queryClient.setQueryData<FileNode[]>(
					["tree"],
					previousTree.map((node) =>
						node.id === variables.id
							? {
									...node,
									title: variables.title ?? node.title,
									isPublished: variables.isPublished ?? node.isPublished,
								}
							: node,
					),
				);
			}

			// Optimistically update the individual note cache
			if (previousNote) {
				queryClient.setQueryData<FileNode>(["getNode", variables.id], {
					...previousNote,
					title: variables.title ?? previousNote.title,
					content: variables.content ?? previousNote.content,
					isPublished: variables.isPublished ?? previousNote.isPublished,
				});
			}

			return { previousTree, previousNote };
		},
		onError: (error: Error, variables, context) => {
			// Rollback to snapshotted values
			if (context?.previousTree) {
				queryClient.setQueryData(["tree"], context.previousTree);
			}
			if (context?.previousNote) {
				queryClient.setQueryData(
					["getNode", variables.id],
					context.previousNote,
				);
			}
			showToast(error.message || "Failed to update note", "error");
		},
		onSuccess: (data, variables) => {
			queryClient.setQueryData(["getNode", data.id], data);

			const treeCaresAboutChanges =
				variables.title !== undefined || variables.isPublished !== undefined;
			if (treeCaresAboutChanges) {
				queryClient.setQueryData<FileNode[]>(["tree"], (oldTree) => {
					if (!oldTree) return [];
					return oldTree.map((node) =>
						node.id === data.id ? { ...node, ...data } : node,
					);
				});
			}
		},
	});
};
